// Notification bell. Toggles the dropdown, fetches its content on open (the
// fetch also records the "Opened Notifications" event server-side, so we don't
// track opens client-side), and wires "Mark all as read" via event delegation.
// The unread badge is server-rendered; item links are plain navigations that go
// through /notifications/:id/click (which marks read + records the click).
//
// It also records viewport impressions: a row is marked "seen" only once it
// actually scrolls into view and dwells briefly (IntersectionObserver) — not
// merely because it was rendered in the list. That feeds the admin Seen funnel.

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";

// Viewport-impression tuning. A row counts as seen when at least SEEN_RATIO of
// it — or SEEN_MIN_PX of its height, for rows taller than their scroll area —
// is visible for SEEN_DWELL_MS, so a fast scroll-past doesn't count.
const SEEN_RATIO = 0.55;
const SEEN_MIN_PX = 200;
const SEEN_DWELL_MS = 350;

const markSeen = (id: string) => {
  fetch(`/notifications/${id}/seen`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" },
    keepalive: true,
  }).catch(() => {
    /* best-effort impression — ignore failures */
  });
};

// Observe every not-yet-seen row within `scope` and mark it seen when it dwells
// in view. `scrollRoot` is the clipping scroll container (the dropdown panel);
// pass null to measure against the viewport (the full history page).
function observeImpressions(scope: ParentNode, scrollRoot: Element | null) {
  if (!("IntersectionObserver" in window)) return;
  const rows = scope.querySelectorAll<HTMLElement>("[data-notification-unseen]");
  if (!rows.length) return;

  const timers = new Map<Element, number>();
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        const visible =
          entry.isIntersecting &&
          (entry.intersectionRatio >= SEEN_RATIO || entry.intersectionRect.height >= SEEN_MIN_PX);
        if (visible) {
          if (timers.has(el)) continue;
          timers.set(
            el,
            window.setTimeout(() => {
              const id = el.dataset.notificationId;
              if (id) markSeen(id);
              el.removeAttribute("data-notification-unseen");
              timers.delete(el);
              io.unobserve(el);
            }, SEEN_DWELL_MS),
          );
        } else {
          const t = timers.get(el);
          if (t !== undefined) {
            clearTimeout(t);
            timers.delete(el);
          }
        }
      }
    },
    { root: scrollRoot, threshold: [0, 0.25, 0.55, 0.8] },
  );

  rows.forEach((row) => io.observe(row));
}

// Full history page: rows are server-rendered and present at load; measure
// against the viewport.
observeImpressions(document, null);

const root = document.querySelector<HTMLElement>("[data-notif-root]");

if (root) {
  const toggle = root.querySelector<HTMLElement>("[data-notif-toggle]")!;
  const panel = root.querySelector<HTMLElement>("[data-notif-panel]")!;
  const content = root.querySelector<HTMLElement>("[data-notif-content]")!;
  const badge = root.querySelector<HTMLElement>("[data-notif-badge]");

  const load = async (refresh = false) => {
    try {
      // `no-store`: never let the browser revalidate this per-user panel with a
      // conditional request — a 304 (empty body) would leave "Loading…" stuck.
      const res = await fetch(refresh ? "/notifications/panel?refresh=1" : "/notifications/panel", {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        cache: "no-store",
      });
      if (res.ok) {
        content.innerHTML = await res.text();
        // Rows just injected: mark seen as they dwell in the panel's own scroll.
        observeImpressions(content, panel);
      }
    } catch {
      /* keep whatever is shown (loading / previous content) */
    }
  };

  // Toggle the `hidden`/`flex` classes rather than the `hidden` attribute — the
  // panel carries a flex layout, and a display utility overrides `[hidden]`.
  const isOpen = () => !panel.classList.contains("hidden");
  const open = () => {
    panel.classList.remove("hidden");
    panel.classList.add("flex");
    toggle.setAttribute("aria-expanded", "true");
    // Stop the bell ring/halo while the panel is open (the `is-open` class kills
    // the animations via a group-[.is-open]: variant — see _notification_bell).
    toggle.classList.add("is-open");
    load();
  };
  const close = () => {
    panel.classList.add("hidden");
    panel.classList.remove("flex");
    toggle.setAttribute("aria-expanded", "false");
    toggle.classList.remove("is-open");
  };

  toggle.addEventListener("click", (e) => {
    e.stopPropagation();
    if (isOpen()) close();
    else open();
  });
  document.addEventListener("click", (e) => {
    if (!root.contains(e.target as Node)) close();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });

  // "Mark all as read" is inside the injected content, so delegate.
  content.addEventListener("click", async (e) => {
    const button = (e.target as HTMLElement).closest("[data-notif-mark-all]");
    if (!button) return;
    e.preventDefault();
    const res = await fetch("/notifications/mark_all_read", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), "X-Requested-With": "XMLHttpRequest" },
    });
    if (res.ok) {
      badge?.classList.add("hidden");
      load(true); // re-render as read, without counting a new open
    }
  });
}
